import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocation as useStoreLocation } from '../contexts/LocationContext'
import LoadingSpinner from '../components/LoadingSpinner'
import ProductForm from '../components/ProductForm'
import StoreSelector from '../components/StoreSelector'
import CameraCapture from '../components/CameraCapture'
import AiProductConfirmModal from '../components/AiProductConfirmModal'
import { Product, CATEGORIES, getBestPrice, isProductAvailableInStore, getProductShelfNumber, PhotoAnalysisResult } from '../types'
import { productsApi } from '../services/api'
import api from '../services/api'
import toast from 'react-hot-toast'

const ProductsPage = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStore, setSelectedStore] = useState<string>('all')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | undefined>(undefined)
  const [showCamera, setShowCamera] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<PhotoAnalysisResult | null>(null)
  const [isCreatingFromAi, setIsCreatingFromAi] = useState(false)
  const location = useLocation()
  const { currentStore } = useStoreLocation()

  useEffect(() => {
    // Check if we should show favorites from URL params
    const urlParams = new URLSearchParams(location.search)
    if (urlParams.get('favorites') === 'true') {
      setShowFavoritesOnly(true)
    }

    loadProducts()
  }, [location.search])

  const loadProducts = async () => {
    setIsLoading(true)
    try {
      const response = await productsApi.getProducts()
      
      if (response.success && response.data) {
        // Backend returns products directly in response.data
        const products = Array.isArray(response.data) ? response.data : []
        setProducts(products)
      } else {
        toast.error(response.error || 'Tuotteiden lataus epäonnistui')
        setProducts([])
      }
    } catch (error) {
      console.error('Failed to load products:', error)
      toast.error('Verkkovirhe')
      setProducts([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteProduct = async (productName: string) => {
    if (!confirm(`Haluatko varmasti poistaa tuotteen "${productName}"?`)) {
      return
    }

    try {
      const response = await productsApi.deleteProduct(productName)
      if (response.success) {
        toast.success('Tuote poistettu')
        loadProducts() // Reload products
      } else {
        toast.error(response.error || 'Tuotteen poisto epäonnistui')
      }
    } catch (error) {
      toast.error('Verkkovirhe')
      console.error('Delete product error:', error)
    }
  }

  const handleCreateProduct = async (productData: Product) => {
    try {
      const response = await productsApi.createProduct({ product: productData })
      if (response.success) {
        toast.success('Tuote lisätty')
        loadProducts() // Reload products
      } else {
        toast.error(response.error || 'Tuotteen lisäys epäonnistui')
        throw new Error(response.error)
      }
    } catch (error) {
      toast.error('Verkkovirhe')
      console.error('Create product error:', error)
      throw error
    }
  }

  const handleUpdateProduct = async (productData: Product) => {
    if (!editingProduct) return
    
    try {
      const response = await productsApi.updateProduct({
        productName: editingProduct.name,
        updates: productData
      })
      if (response.success) {
        toast.success('Tuote päivitetty')
        loadProducts() // Reload products
        setEditingProduct(undefined)
      } else {
        toast.error(response.error || 'Tuotteen päivitys epäonnistui')
        throw new Error(response.error)
      }
    } catch (error) {
      toast.error('Verkkovirhe')
      console.error('Update product error:', error)
      throw error
    }
  }

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingProduct(undefined)
  }

  const handlePhotoCapture = async (imageData: string) => {
    setShowCamera(false)
    setIsAnalyzing(true)
    
    try {
      // Use current location store if set, otherwise fall back to selected store or default
      const storeForAnalysis = currentStore || (selectedStore === 'all' ? 'K-Market' : selectedStore)
      
      const response = await api.ai.analyzePhoto({
        imageData,
        store: storeForAnalysis,
      })

      if (response.success && response.data) {
        setAnalysisResult(response.data)
        setIsAnalyzing(false)
      } else {
        toast.error(response.error || 'Kuvan analysointi epäonnistui')
        setIsAnalyzing(false)
      }
    } catch (error) {
      toast.error('Verkkovirhe kuvaa analysoidessa')
      console.error('Photo analysis error:', error)
      setIsAnalyzing(false)
    }
  }

  const handleAiProductConfirm = async (productData: Omit<Product, 'prices'> & {
    store: string
    price: number
    shelfNumber?: string
    storeName?: string
  }) => {
    setIsCreatingFromAi(true)
    
    try {
      // Convert AI product data to regular Product format
      const product: Product = {
        name: productData.name,
        category: productData.category,
        isFavorite: productData.isFavorite,
        prices: [{
          store: productData.store,
          price: productData.price,
          shelfNumber: productData.shelfNumber,
          storeName: productData.storeName
        }]
      }

      await handleCreateProduct(product)
      setAnalysisResult(null)
      toast.success('Tuote luotu AI-analyysin perusteella!')
    } catch (error) {
      // Error handling is done in handleCreateProduct
    } finally {
      setIsCreatingFromAi(false)
    }
  }

  const handleCancelAiProduct = () => {
    setAnalysisResult(null)
    setIsAnalyzing(false)
  }

  const handleCancelCamera = () => {
    setShowCamera(false)
    setIsAnalyzing(false)
  }

  const filteredProducts = (products || []).filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStore = selectedStore === 'all' || isProductAvailableInStore(product, selectedStore)
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory
    const matchesFavorites = !showFavoritesOnly || product.isFavorite
    
    return matchesSearch && matchesStore && matchesCategory && matchesFavorites
  })

  const getDisplayPrice = (product: Product) => {
    if (selectedStore === 'all') {
      const best = getBestPrice(product)
      const storePrice = product.prices.find(p => p.store === best.store)
      return {
        price: best.price,
        store: best.store,
        storeName: storePrice?.storeName,
        isBestPrice: true
      }
    } else {
      const storePrice = product.prices.find(p => p.store === selectedStore)
      if (storePrice) {
        return {
          price: storePrice.price,
          store: selectedStore,
          storeName: storePrice.storeName,
          isBestPrice: false
        }
      } else {
        // Show best price with indication it's from different store
        const best = getBestPrice(product)
        const bestStorePrice = product.prices.find(p => p.store === best.store)
        return {
          price: best.price,
          store: best.store,
          storeName: bestStorePrice?.storeName,
          isBestPrice: false,
          isDifferentStore: true
        }
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Tuotteet
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Hallitse tuotteita ja niiden tietoja
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex gap-2">
            <button
              onClick={() => setShowCamera(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89L8.94 4.66A2 2 0 0110.604 4h2.792a2 2 0 011.664.66L16.4 6.11A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              AI-kuva
            </button>
            <button
              onClick={() => setIsFormOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Lisää tuote
            </button>
          </div>
        </div>
        
        {/* Store Selector */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <StoreSelector 
            selectedStore={selectedStore}
            onStoreChange={setSelectedStore}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Haku
            </label>
            <input
              id="search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Etsi tuotteita..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Kategoria
            </label>
            <select
              id="category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Kaikki kategoriat</option>
              {CATEGORIES.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showFavoritesOnly}
                onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Vain suosikit</span>
            </label>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Ei tuotteita</h3>
            <p className="mt-1 text-sm text-gray-500">Aloita lisäämällä ensimmäinen tuote.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tuote
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hinta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kauppa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hyllynro
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kiertoaika
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Toiminnot</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map((product, index) => {
                  const displayPrice = getDisplayPrice(product)
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">
                            {product.name}
                          </div>
                          {product.isFavorite && (
                            <svg className="ml-2 h-4 w-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className={displayPrice.isBestPrice ? 'font-semibold text-green-600' : ''}>
                            {displayPrice.price.toFixed(2)} €
                          </span>
                          {displayPrice.isBestPrice && (
                            <span className="ml-1 text-xs text-green-600">(paras)</span>
                          )}
                          {displayPrice.isDifferentStore && (
                            <span className="ml-1 text-xs text-orange-600">(eri kauppa)</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <span className={displayPrice.isDifferentStore ? 'text-orange-600' : ''}>
                            {displayPrice.store}
                          </span>
                          {displayPrice.storeName && (
                            <div className="text-xs text-gray-600">
                              {displayPrice.storeName}
                            </div>
                          )}
                          {selectedStore !== 'all' && product.prices.length > 1 && (
                            <div className="text-xs text-gray-500">
                              {product.prices.length} kaupassa
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getProductShelfNumber(product, selectedStore)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product.rotationDays ? `${product.rotationDays} pv` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          onClick={() => handleEditProduct(product)}
                          className="text-primary-600 hover:text-primary-900 mr-3"
                        >
                          Muokkaa
                        </button>
                        <button 
                          onClick={() => handleDeleteProduct(product.name)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Poista
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      <ProductForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSave={editingProduct ? handleUpdateProduct : handleCreateProduct}
        product={editingProduct}
        isEditing={!!editingProduct}
      />

      {/* AI Photo Capture Modal */}
      {showCamera && (
        <CameraCapture
          onCapture={handlePhotoCapture}
          onClose={handleCancelCamera}
          isAnalyzing={isAnalyzing}
        />
      )}

      {/* AI Product Confirmation Modal */}
      {analysisResult && (
        <AiProductConfirmModal
          analysisResult={analysisResult}
          store={currentStore || (selectedStore === 'all' ? 'K-Market' : selectedStore)}
          onConfirm={handleAiProductConfirm}
          onCancel={handleCancelAiProduct}
          isCreating={isCreatingFromAi}
        />
      )}
    </div>
  )
}

export default ProductsPage 